import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Chip, Tooltip, CircularProgress,
    useTheme, Collapse, LinearProgress, IconButton, TextField,
} from '@mui/material';
import {
    CheckCircle as DoneIcon,
    RadioButtonUnchecked as PendingIcon,
    RemoveCircleOutline as SkippedIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { PlaybookSession, PlaybookSessionStep, StepStatus, StepType } from '../types';
import { PlaybooksService } from '../services/playbooksService';
import { colorUtils } from '../theme';

// ── Step type badge config ──────────────────────────────────────────────────

const STEP_TYPE_CONFIG: Record<StepType, { label: string; color: string }> = {
    observe: { label: 'OBSERVE', color: '#4fc3f7' },
    decide:  { label: 'DECIDE',  color: '#ffb74d' },
    act:     { label: 'ACT',     color: '#ef9a9a' },
};

// ── Phase ordering ──────────────────────────────────────────────────────────

const PHASE_ORDER = [
    'Detection & Analysis',
    'Containment',
    'Eradication & Recovery',
    'Post-Incident',
];

const PHASE_COLORS: Record<string, string> = {
    'Detection & Analysis': '#4fc3f7',
    'Containment': '#ff8a65',
    'Eradication & Recovery': '#81c784',
    'Post-Incident': '#ce93d8',
};

// ── Step status helpers ─────────────────────────────────────────────────────

function nextStatus(current: StepStatus): StepStatus {
    if (current === 'pending') return 'done';
    if (current === 'done') return 'skipped';
    return 'pending';
}

const StatusIcon: React.FC<{ status: StepStatus; size?: number }> = ({ status, size = 18 }) => {
    const theme = useTheme();
    if (status === 'done') return <DoneIcon sx={{ fontSize: size, color: theme.palette.success.main }} />;
    if (status === 'skipped') return <SkippedIcon sx={{ fontSize: size, color: theme.palette.text.disabled }} />;
    return <PendingIcon sx={{ fontSize: size, color: theme.palette.text.tertiary }} />;
};

// ── Single step row ─────────────────────────────────────────────────────────

const StepRow: React.FC<{
    step: PlaybookSessionStep;
    onStatusChange: (stepId: string, status: StepStatus, notes?: string) => Promise<void>;
}> = ({ step, onStatusChange }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [localNotes, setLocalNotes] = useState(step.notes ?? '');
    const isDone = step.status === 'done';
    const isSkipped = step.status === 'skipped';

    const handleToggleStatus = async () => {
        setSaving(true);
        try {
            await onStatusChange(step.id, nextStatus(step.status));
        } finally {
            setSaving(false);
        }
    };

    const handleNotesBlur = async () => {
        if (localNotes !== (step.notes ?? '')) {
            await onStatusChange(step.id, step.status, localNotes);
        }
    };

    return (
        <Box sx={{
            mb: 0.75,
            borderRadius: 1.5,
            border: `1px solid ${isDone
                ? colorUtils.withOpacity(theme.palette.success.main, 0.2)
                : colorUtils.withOpacity(theme.palette.divider, 0.6)}`,
            backgroundColor: isDone
                ? colorUtils.withOpacity(theme.palette.success.main, 0.04)
                : isSkipped
                    ? colorUtils.withOpacity(theme.palette.text.disabled, 0.04)
                    : theme.palette.background.paper,
            opacity: isSkipped ? 0.6 : 1,
            transition: 'all 0.2s ease',
        }}>
            {/* Main row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 1.5, py: 1 }}>
                {/* Status toggle */}
                <Box sx={{ mt: 0.25, cursor: 'pointer', flexShrink: 0 }} onClick={handleToggleStatus}>
                    {saving
                        ? <CircularProgress size={18} />
                        : <StatusIcon status={step.status} />}
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            flexShrink: 0,
                        }}>
                            {String(step.step_number).padStart(2, '0')}
                        </Typography>
                        {step.step_type && STEP_TYPE_CONFIG[step.step_type] && (
                            <Typography variant="caption" sx={{
                                fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em',
                                px: 0.6, py: 0.1, borderRadius: 0.5,
                                color: STEP_TYPE_CONFIG[step.step_type].color,
                                backgroundColor: colorUtils.withOpacity(STEP_TYPE_CONFIG[step.step_type].color, 0.12),
                                flexShrink: 0,
                            }}>
                                {STEP_TYPE_CONFIG[step.step_type].label}
                            </Typography>
                        )}
                        {step.is_required && (
                            <Typography variant="caption" sx={{
                                fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em',
                                px: 0.6, py: 0.1, borderRadius: 0.5,
                                color: theme.palette.error.main,
                                backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.1),
                                flexShrink: 0,
                            }}>
                                REQ
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{
                            color: isSkipped ? theme.palette.text.disabled : theme.palette.text.primary,
                            fontSize: '0.8rem',
                            fontWeight: isDone ? 400 : 500,
                            textDecoration: isSkipped ? 'line-through' : 'none',
                            lineHeight: 1.4,
                        }}>
                            {step.action}
                        </Typography>
                    </Box>
                    {step.responsible && (
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            ml: '2.2rem',
                        }}>
                            {step.responsible}
                        </Typography>
                    )}
                </Box>

                {/* Expand toggle */}
                {(step.what_to_check || step.expected_output) && (
                    <IconButton size="small" onClick={() => setExpanded(e => !e)} sx={{ flexShrink: 0 }}>
                        {expanded
                            ? <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.tertiary }} />
                            : <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.tertiary }} />}
                    </IconButton>
                )}
            </Box>

            {/* Expanded detail */}
            <Collapse in={expanded}>
                <Box sx={{ px: 2.5, pb: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {step.what_to_check && (
                        <Box>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.tertiary,
                                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                                display: 'block', mb: 0.25,
                            }}>
                                WHAT TO CHECK
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                {step.what_to_check}
                            </Typography>
                        </Box>
                    )}
                    {step.expected_output && (
                        <Box>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.tertiary,
                                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                                display: 'block', mb: 0.25,
                            }}>
                                EXPECTED OUTPUT
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                {step.expected_output}
                            </Typography>
                        </Box>
                    )}
                    {step.primary_technology && (
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary, fontSize: '0.65rem',
                            fontFamily: '"JetBrains Mono", monospace',
                        }}>
                            {step.primary_technology}
                        </Typography>
                    )}
                    <TextField
                        size="small"
                        multiline
                        maxRows={3}
                        placeholder="Add notes…"
                        value={localNotes}
                        onChange={e => setLocalNotes(e.target.value)}
                        onBlur={handleNotesBlur}
                        sx={{ mt: 0.5 }}
                        inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                </Box>
            </Collapse>
        </Box>
    );
};

// ── Phase block ─────────────────────────────────────────────────────────────

const PhaseBlock: React.FC<{
    phase: string;
    steps: PlaybookSessionStep[];
    onStatusChange: (stepId: string, status: StepStatus, notes?: string) => Promise<void>;
}> = ({ phase, steps, onStatusChange }) => {
    const theme = useTheme();
    const color = PHASE_COLORS[phase] ?? theme.palette.primary.main;
    const done = steps.filter(s => s.status === 'done' || s.status === 'skipped').length;
    const total = steps.length;
    const pct = total ? Math.round((done / total) * 100) : 0;

    return (
        <Box sx={{ mb: 2.5 }}>
            {/* Phase header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 3, height: 16, borderRadius: 1, backgroundColor: color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{
                    color, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1,
                }}>
                    {phase.toUpperCase()}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem' }}>
                    {done}/{total}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    mb: 1.5, height: 2, borderRadius: 1,
                    backgroundColor: colorUtils.withOpacity(color, 0.12),
                    '& .MuiLinearProgress-bar': { backgroundColor: color },
                }}
            />
            {steps.map(step => (
                <StepRow key={step.id} step={step} onStatusChange={onStatusChange} />
            ))}
        </Box>
    );
};

// ── Main panel ──────────────────────────────────────────────────────────────

interface Props {
    session: PlaybookSession;
    onSessionRefresh: () => void;
}

const DDoSPlaybookPanel: React.FC<Props> = ({ session, onSessionRefresh }) => {
    const theme = useTheme();
    const [steps, setSteps] = useState<PlaybookSessionStep[]>([]);
    const [loading, setLoading] = useState(true);

    const loadSteps = useCallback(async () => {
        try {
            const data = await PlaybooksService.getSessionSteps(session.id);
            setSteps(data);
        } catch {
            setSteps([]);
        } finally {
            setLoading(false);
        }
    }, [session.id]);

    useEffect(() => { loadSteps(); }, [loadSteps]);

    const handleStatusChange = async (stepId: string, status: StepStatus, notes?: string) => {
        const updated = await PlaybooksService.updateSessionStep(session.id, stepId, {
            status, ...(notes !== undefined ? { notes } : {}),
        });
        setSteps(prev => prev.map(s => s.id === stepId ? updated : s));
        // Refresh session so AI summary updates when it comes back from background thread
        setTimeout(onSessionRefresh, 8000);
    };

    // Group by phase in canonical order
    const byPhase = steps.reduce<Record<string, PlaybookSessionStep[]>>((acc, step) => {
        (acc[step.phase] ??= []).push(step);
        return acc;
    }, {});

    const orderedPhases = [
        ...PHASE_ORDER.filter(p => byPhase[p]),
        ...Object.keys(byPhase).filter(p => !PHASE_ORDER.includes(p)),
    ];

    const totalDone = steps.filter(s => s.status === 'done' || s.status === 'skipped').length;
    const totalSteps = steps.length;
    const requiredSteps = steps.filter(s => s.is_required);
    const requiredDone = requiredSteps.filter(s => s.status === 'done' || s.status === 'skipped').length;
    const overallPct = totalSteps ? Math.round((totalDone / totalSteps) * 100) : 0;

    if (loading) return <CircularProgress size={20} sx={{ m: 2 }} />;

    return (
        <Box>
            {/* AI next steps */}
            {session.ai_next_steps && session.ai_next_steps.length > 0 && (
                <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.info.main, 0.05), border: `1px solid ${colorUtils.withOpacity(theme.palette.info.main, 0.12)}` }}>
                    <Typography variant="caption" sx={{
                        color: theme.palette.info.main,
                        fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
                        display: 'block', mb: 1,
                    }}>
                        AI RECOMMENDED ACTIONS
                    </Typography>
                    {session.ai_next_steps.map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1.25, mb: idx < session.ai_next_steps!.length - 1 ? 1.25 : 0 }}>
                            <Box sx={{
                                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: 0.1,
                                backgroundColor: colorUtils.withOpacity(theme.palette.info.main, 0.15),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: theme.palette.info.main }}>
                                    {idx + 1}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontSize: '0.78rem', fontWeight: 500, lineHeight: 1.4, display: 'block' }}>
                                    {item.step}
                                </Typography>
                                {item.rationale && (
                                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.7rem', lineHeight: 1.4, display: 'block', mt: 0.25 }}>
                                        {item.rationale}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Overall progress */}
            {totalSteps > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={overallPct}
                            sx={{
                                flex: 1, height: 4, borderRadius: 2,
                                backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1),
                            }}
                        />
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', flexShrink: 0 }}>
                            {totalDone}/{totalSteps}
                        </Typography>
                    </Box>
                    {requiredSteps.length > 0 && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.62rem' }}>
                            {requiredDone}/{requiredSteps.length} required
                        </Typography>
                    )}
                </Box>
            )}

            {/* Phase blocks */}
            {orderedPhases.map(phase => (
                <PhaseBlock
                    key={phase}
                    phase={phase}
                    steps={byPhase[phase]}
                    onStatusChange={handleStatusChange}
                />
            ))}
        </Box>
    );
};

export default DDoSPlaybookPanel;
